import React, { useEffect, useState } from "react";
import { Button, message, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import usePost from "../../hooks/usePost";
import usePut from "../../hooks/usePut";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import styles from "./CoachForm.module.scss";
import {
  CoachApplicationReviewRequest,
  CoachApplicationReviewResponse,
  CoachFileKey,
  COACH_FILE_URL_KEYS,
  REQUIRED_COACH_FILE_KEYS,
} from "../../types/coachApplication";

interface FileState {
  photo: File | null;
  medicalCertificate: File | null;
  noConvictionProof: File | null;
  oldLicense: File | null;
  diplomaCopy: File | null;
}

export interface CoachStepFourReview {
  token: string;
  application: CoachApplicationReviewRequest;
  documents: Pick<
    CoachApplicationReviewResponse,
    | "photoUrl"
    | "medicalCertificateUrl"
    | "noConvictionProofUrl"
    | "oldLicenseUrl"
    | "diplomaCopyUrl"
  >;
  onSuccess: () => void;
  onBack?: () => void;
  validateAndGetApplication?: () => Promise<CoachApplicationReviewRequest>;
}

interface CoachStepFourProps {
  courseId: string;
  review?: CoachStepFourReview;
}

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

const FILE_TYPE_RULES: Record<keyof FileState, { types: string[]; accept: string; messageKey: string }> = {
  photo: {
    types: ["image/jpeg", "image/jpg", "image/png"],
    accept: ".jpg,.jpeg,.png",
    messageKey: "app.validations.fileTypeImage",
  },
  medicalCertificate: {
    types: ["application/pdf"],
    accept: ".pdf",
    messageKey: "app.validations.fileTypePdf",
  },
  noConvictionProof: {
    types: ["application/pdf"],
    accept: ".pdf",
    messageKey: "app.validations.fileTypePdf",
  },
  oldLicense: {
    types: ["application/pdf"],
    accept: ".pdf",
    messageKey: "app.validations.fileTypePdf",
  },
  diplomaCopy: {
    types: ["application/pdf"],
    accept: ".pdf",
    messageKey: "app.validations.fileTypePdf",
  },
};

const CoachStepFour = ({ courseId, review }: CoachStepFourProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { post, loading: applying } = usePost("/coach-applications/apply");
  const reviewUrl = review
    ? `/coach-applications/review/${encodeURIComponent(review.token)}`
    : "/coach-applications/review/";
  const { put, loading: reviewing } = usePut<CoachApplicationReviewResponse>(reviewUrl);
  const loading = applying || reviewing;

  const [files, setFiles] = useState<FileState>({
    photo: null,
    medicalCertificate: null,
    noConvictionProof: null,
    oldLicense: null,
    diplomaCopy: null,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FileState, string>>>({});

  // Guard against landing on step 4 directly (refresh, back button, deep link)
  // without having completed the earlier steps. Review mode already has the
  // application loaded from the token, so the sessionStorage check is skipped.
  useEffect(() => {
    if (review) return;
    const hasStepTwo = sessionStorage.getItem("coachStepTwo");
    const hasStepThree = sessionStorage.getItem("coachStepThree");
    if (!hasStepTwo || !hasStepThree) {
      message.warning(t("app.coaches.missingStepsData"));
      navigate(-1); // adjust to a specific step route if you have one, e.g. navigate(`/coach-application/${courseId}/step-2`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasActiveErrors = Object.values(errors).some((err) => Boolean(err));
    if (hasActiveErrors) {
      validate();
    }
  }, [i18n.language]);

  const handleFileChange = (key: keyof FileState, file: File) => {
    const rule = FILE_TYPE_RULES[key];
    if (!rule.types.includes(file.type)) {
      message.error(t(rule.messageKey));
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      message.error(t("app.validations.fileSize"));
      return Upload.LIST_IGNORE;
    }

    setFiles((prev) => ({ ...prev, [key]: file }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    return false; // prevent antd auto-upload
  };

  const existingUrlFor = (key: CoachFileKey) =>
    review ? review.documents[COACH_FILE_URL_KEYS[key]] : null;

  const isFileRequired = (key: CoachFileKey) => {
    if (!REQUIRED_COACH_FILE_KEYS.includes(key)) return false;
    if (!review) return true;
    return !existingUrlFor(key);
  };

  const validate = () => {
    const newErrors: Partial<Record<keyof FileState, string>> = {};
    REQUIRED_COACH_FILE_KEYS.forEach((key) => {
      if (isFileRequired(key) && !files[key]) {
        newErrors[key] = t("app.validations.required");
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const appendFiles = (formData: FormData) => {
    if (files.photo) formData.append("photo", files.photo);
    if (files.medicalCertificate) formData.append("medicalCertificate", files.medicalCertificate);
    if (files.noConvictionProof) formData.append("noConvictionProof", files.noConvictionProof);
    if (files.oldLicense) formData.append("oldLicense", files.oldLicense);
    if (files.diplomaCopy) formData.append("diplomaCopy", files.diplomaCopy);
  };

  const onSubmit = async () => {
    if (!validate()) return;

    if (review) {
      try {
        const applicationPayload = review.validateAndGetApplication
          ? await review.validateAndGetApplication()
          : review.application;
        const formData = new FormData();
        formData.append(
          "application",
          new Blob([JSON.stringify(applicationPayload)], { type: "application/json" })
        );
        appendFiles(formData);
        await put(formData);
        review.onSuccess();
      } catch (err) {
        if (err && typeof err === "object" && "errorFields" in err) {
          return;
        }
        const msg = err instanceof Error ? err.message : undefined;
        message.error(msg || t("app.coachApplicationReview.submitError"));
      }
      return;
    }

    if (!executeRecaptcha) {
      message.error(t("app.coaches.recaptchaNotReady"));
      return;
    }

    try {
      const token = await executeRecaptcha("submit");

      const stepTwoData = JSON.parse(sessionStorage.getItem("coachStepTwo") || "{}");
      const stepThreeData = JSON.parse(sessionStorage.getItem("coachStepThree") || "{}");

      // Signature disabled for the moment — kept for future re-enable.
      // const { signature, ...careerData } = stepThreeData;
      // if (signature) {
      //   const res = await fetch(signature);
      //   const blob = await res.blob();
      //   formData.append("signature", blob, "signature.png");
      // }

      // recaptchaToken and courseId are spread last so that neither can be
      // silently overwritten if stepTwoData/stepThreeData happen to contain
      // keys with the same name.
      const application = {
        ...stepTwoData,
        ...stepThreeData,
        recaptchaToken: token,
        courseId,
      };

      const formData = new FormData();
      formData.append(
          "application",
          new Blob([JSON.stringify(application)], { type: "application/json" })
      );

      appendFiles(formData);

      await post(formData);

      sessionStorage.removeItem("coachStepTwo");
      sessionStorage.removeItem("coachStepThree");

      message.success(t("app.coaches.applicationSubmitted"));
      navigate("/");
    } catch (err) {
      console.error("Coach application submission error:", err);
      message.error(t("app.coaches.applicationError"));
    }
  };

  const uploadFields: {
    key: keyof FileState;
    label: string;
    required: boolean;
  }[] = [
    { key: "photo", label: t("app.coaches.fields.photo", "Fotografia"), required: true },
    {
      key: "medicalCertificate",
      label: t("app.coaches.fields.medicalCertificate", "Çertifikatë shëndetësore"),
      required: true,
    },
    {
      key: "noConvictionProof",
      label: t(
          "app.coaches.fields.noConvictionProof",
          "Dëshmi që aplikanti nuk është i dënuar dhe nuk është nën hetime"
      ),
      required: true,
    },
    { key: "oldLicense", label: t("app.coaches.fields.oldLicense", "Licenca e vjetër"), required: false },
    {
      key: "diplomaCopy",
      label: t("app.coaches.fields.diplomaCopy", "Kopje e diplomës së shkollimit të fundit"),
      required: true,
    },
  ];

  return (
      <div className={`${styles.CoachFormContainer} ${review ? styles.CoachFormContainerReview : ""}`}>
          <div className={styles.CoachFormTitle}>
            <h1>{t("app.coaches.stepFour.title")}</h1>
            <p>{t("app.coaches.stepFour.subtitle")}</p>
          </div>

          <div className={styles.CoachForm}>
            {uploadFields.map(({ key, label, required }) => {
              const existingUrl = existingUrlFor(key);
              const hasReplacement = Boolean(files[key]);
              return (
                <div key={key} className={`${styles.UploadField} ${review ? styles.UploadFieldReview : ""}`}>
                  <p>
                    <span>
                      {label}
                      {required && <span className={styles.RequiredMark}> *</span>}
                      {!required && <span className={styles.OptionalMark}> ({t("app.optional")})</span>}
                    </span>
                    {review && (
                      hasReplacement ? (
                        <span className={styles.DocumentStatusOk}>
                          {t("app.coachApplicationReview.documentReplaced")}
                        </span>
                      ) : existingUrl ? (
                        <a
                          href={existingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.DocumentStatusOk}
                        >
                          {t("app.coachApplicationReview.documentExisting")}
                        </a>
                      ) : required ? (
                        <span className={styles.DocumentStatusMissing}>
                          {t("app.coachApplicationReview.documentMissing")}
                        </span>
                      ) : (
                        <span className={styles.DocumentStatusOptional}>
                          {t("app.coachApplicationReview.documentOptionalMissing")}
                        </span>
                      )
                    )}
                  </p>
                  <div className={styles.UploadFieldAction}>
                    <Upload
                        accept={FILE_TYPE_RULES[key].accept}
                        beforeUpload={(file) => handleFileChange(key, file)}
                        maxCount={1}
                        showUploadList={{ showRemoveIcon: true }}
                        onRemove={() => setFiles((prev) => ({ ...prev, [key]: null }))}
                    >
                      <Button icon={<UploadOutlined />}>
                        {files[key] ? files[key]!.name : t("app.chooseFile")}
                      </Button>
                    </Upload>
                    {errors[key] && <div className={styles.FieldError}>{errors[key]}</div>}
                  </div>
                </div>
              );
            })}
          </div>

        <div className={styles.ButtonAlignment}>
          {review?.onBack && (
            <Button
              onClick={review.onBack}
              className={`${styles.ButtonStyle} ${styles.ButtonBack}`}
              disabled={loading}
            >
              {t("app.coaches.back", "Kthehu")}
            </Button>
          )}
          <Button type="primary" className={styles.ButtonStyle} onClick={onSubmit} loading={loading} disabled={loading}>
            {review
              ? t("app.coachApplicationReview.submit", "Ridërgo aplikimin")
              : t("app.coaches.submitApplication", "Dërgo aplikimin")}
          </Button>
        </div>
      </div>
  );
};

export default CoachStepFour;